import * as React from 'react'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import type { Integration, IntegrationService } from '@axilon-apps/domains-ontology-ui-state'

import { Button } from '../../../components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../../../components/ui/dialog'
import { Input } from '../../../components/ui/input'
import { Label } from '../../../components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../../components/ui/select'
import { Switch } from '../../../components/ui/switch'
import { extractApiError } from './errors'
import { INTEGRATION_PROVIDERS, getIntegrationProvider } from './providers'
import { useIntegrationCredentialFlows } from './useIntegrationCredentialFlows'

type DialogMode = 'create' | 'edit'

interface IntegrationFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  mode: DialogMode
  /** Required when `mode === 'edit'`. */
  integration?: Integration
}

const DEFAULT_SERVICE: IntegrationService = INTEGRATION_PROVIDERS[0]?.service ?? 'servicenow'

export function IntegrationFormDialog({
  open,
  onOpenChange,
  mode,
  integration
}: IntegrationFormDialogProps) {
  const { createIntegrationWithSecret, updateIntegrationWithSecret, isCreating, isUpdating } =
    useIntegrationCredentialFlows()

  const [service, setService] = React.useState<IntegrationService>(
    integration?.service ?? DEFAULT_SERVICE
  )
  const [instanceName, setInstanceName] = React.useState(integration?.instance_name ?? '')
  const [apiKey, setApiKey] = React.useState('')
  const [workOrdersEnabled, setWorkOrdersEnabled] = React.useState(
    Boolean(integration?.work_orders_enabled)
  )
  const [formError, setFormError] = React.useState('')

  // Reset the form whenever the dialog (re)opens or its subject changes,
  // so a stale draft never bleeds across open/close cycles.
  React.useEffect(() => {
    if (open) {
      setService(integration?.service ?? DEFAULT_SERVICE)
      setInstanceName(integration?.instance_name ?? '')
      setApiKey('')
      setWorkOrdersEnabled(Boolean(integration?.work_orders_enabled))
      setFormError('')
    }
  }, [open, integration])

  const provider = getIntegrationProvider(service)
  const busy = mode === 'create' ? isCreating : isUpdating
  const trimmedInstance = instanceName.trim()
  const showWorkOrdersToggle = service === 'servicenow'

  const apiKeyRequired = mode === 'create' && (provider?.requiresApiKey ?? false)
  const canSubmit =
    !!provider &&
    trimmedInstance.length > 0 &&
    (!apiKeyRequired || apiKey.trim().length > 0) &&
    !busy

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!provider || !canSubmit) return
    setFormError('')

    try {
      if (mode === 'create') {
        await createIntegrationWithSecret({
          service,
          instanceName: trimmedInstance,
          apiKey,
          workOrdersEnabled: showWorkOrdersToggle ? workOrdersEnabled : undefined
        })
        toast(`${provider.label} integration added`)
      } else if (integration) {
        await updateIntegrationWithSecret({
          integration,
          instanceName: trimmedInstance,
          newApiKey: apiKey,
          workOrdersEnabled: showWorkOrdersToggle ? workOrdersEnabled : undefined
        })
        toast(`${provider.label} integration updated`)
      }
      onOpenChange(false)
    } catch (err) {
      setFormError(
        extractApiError(
          err,
          mode === 'create' ? 'Could not add integration.' : 'Could not update integration.'
        )
      )
    }
  }

  const title = mode === 'create' ? 'Add integration' : `Edit ${provider?.label ?? 'integration'}`
  const description =
    mode === 'create'
      ? 'Connect to an external data provider. Credentials are stored encrypted and write-only.'
      : 'Update the instance name or rotate the API key. Leave the key blank to keep the current one.'

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          {mode === 'create' ? (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="integration-service">Service</Label>
              <Select
                value={service}
                onValueChange={(v) => setService(v as IntegrationService)}
                disabled={INTEGRATION_PROVIDERS.length <= 1}
              >
                <SelectTrigger id="integration-service" className="w-full" aria-label="Service">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {INTEGRATION_PROVIDERS.map((p) => (
                    <SelectItem key={p.service} value={p.service}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div className="flex flex-col gap-1.5">
              <Label>Service</Label>
              <div className="flex h-9 items-center rounded-md border border-input bg-muted/40 px-3 text-sm text-ink">
                {provider?.label ?? service}
              </div>
            </div>
          )}

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="integration-instance">
              {provider?.instanceLabel ?? 'Instance name'}
            </Label>
            <Input
              id="integration-instance"
              value={instanceName}
              placeholder={provider?.instancePlaceholder}
              onChange={(e) => {
                setInstanceName(e.target.value)
                setFormError('')
              }}
              autoComplete="off"
            />
            {provider?.instanceHelp ? (
              <p className="text-xs text-ink-3">{provider.instanceHelp}</p>
            ) : null}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="integration-api-key">{provider?.apiKeyLabel ?? 'API key'}</Label>
            <Input
              id="integration-api-key"
              type="password"
              value={apiKey}
              placeholder={
                mode === 'edit' ? 'Leave blank to keep current key' : provider?.apiKeyPlaceholder
              }
              onChange={(e) => {
                setApiKey(e.target.value)
                setFormError('')
              }}
              autoComplete="new-password"
            />
            {provider?.apiKeyHelp ? (
              <p className="text-xs text-ink-3">{provider.apiKeyHelp}</p>
            ) : null}
          </div>

          {showWorkOrdersToggle ? (
            <div className="flex flex-col gap-2 rounded-md border border-line px-3 py-2.5">
              <p className="text-xs font-medium uppercase tracking-wide text-ink-3">Options</p>
              <div className="flex items-start justify-between gap-3">
                <div className="flex min-w-0 flex-col gap-0.5">
                  <Label htmlFor="integration-work-orders" className="text-sm font-medium text-ink">
                    Show in technician work orders
                  </Label>
                  <p className="text-xs text-ink-3">
                    When on, open incidents from this ServiceNow instance appear in the Work Orders
                    list so technicians can view, complete, and comment on them from Axilon. This
                    does not create new tickets in ServiceNow — agent-created work orders remain
                    internal for now.
                  </p>
                </div>
                <Switch
                  id="integration-work-orders"
                  checked={workOrdersEnabled}
                  onCheckedChange={setWorkOrdersEnabled}
                  aria-label="Show in technician work orders"
                />
              </div>
            </div>
          ) : null}

          {formError ? (
            <div role="alert" className="rounded-md bg-st-down-bg px-3 py-2 text-xs text-st-down">
              {formError}
            </div>
          ) : null}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {busy ? <Loader2 className="animate-spin" /> : null}
              {mode === 'create' ? 'Add integration' : 'Save changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
